import React, {useState, useEffect} from 'react'
import ReactDOM from 'react-dom';
import MuiAlert from '@mui/material/Alert';
import { Box, Typography, Button, Select, MenuItem, Checkbox, ListItemText, TextField } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/system';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { useNavigate, useLocation } from 'react-router-dom';
import { jaJP } from '@mui/x-data-grid/locales';
import { userURL, apiURL } from './App';
import { authFetch, authJson } from './apiClient';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import theme from './theme';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toDateFormat } from './Controls';
import { min } from 'date-fns';

// コンテナ
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  //padding: '30px',
  backgroundColor: '#fff',
  position: 'relative',
  width: '100%',
  height: '100%'
});

// DataGrid
export const ThemeDataGrid = styled(DataGrid)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.main,
    outline: 'none',
    boxShadow: 'none',
  },
  // 列メニュー（…）を完全に非表示
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon, & .MuiDataGrid-iconButtonContainer': {
    display: 'none !important',
  },
  // ヘッダ列のフォーカス枠を非表示
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
    boxShadow: 'none',
  },
  '& .MuiDataGrid-filler': {
    backgroundColor: theme.palette.secondary.light,
  },
  '& .MuiDataGrid-root': {
    backgroundColor: theme.palette.secondary.light,
  },
  // セルクリック時の枠を非表示
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  // 編集セルのinputを角丸を指定
  '& .MuiDataGrid-cell--editing input': {
    height: '32px', // Date入力と同じ高さ
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: '5px',
    paddingRight: '5px',
    borderRadius: '5px',
  },
  '& .MuiDataGrid-cell--editing': {
    padding: 0,
    overflow: 'visible',
  },
  // 先頭の空白行プレースホルダ用
  '& .placeholder-row': {
    position: 'relative',
  },
  '& .placeholder-row::after': {
    content: '"この行に新規予定を入力してください"',
    position: 'absolute',
    left: 12,
    right: 12,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    pointerEvents: 'none',
  },
  '& .MuiDataGrid-row--editing.placeholder-row::after': {
    display: 'none',
  },
  // 行ホバー時のみ表示するアクションボタン
  '& .row-action': {
    visibility: 'hidden',
  },
  '& .MuiDataGrid-row:hover .row-action': {
    visibility: 'visible',
  },
  // 行が編集モードの間は常に非表示（ホバー時も）
  '& .MuiDataGrid-row--editing .row-action': {
    visibility: 'hidden',
  },
  '& .MuiDataGrid-row--editing:hover .row-action': {
    visibility: 'hidden',
  },
  // actions列のセル全体をフレックス中央揃え
  '& .MuiDataGrid-cell[data-field="actions"]': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
}));

// DataGrid用: 時刻入力共通エディタ
const TimeEditCell = (params) => (
  <input
    type="time"
    value={params.value || ''}
    onChange={e => params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value })}
    style={{
      width: '100%',
      height: '36px', // Date入力と同じ高さ
      fontSize: '11pt',
      fontFamily: theme.typography.fontFamily,
      textAlign: 'center',
      alignSelf: 'center',
      display: 'flex',
      alignItems: 'center',
      borderRadius: '5px',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      boxShadow: 'none',
      color: '#222',
      transition: 'border 0.2s',
    }}
  />
);

// mark編集セル用コンポーネント
function MarkEditCell(params) {
  const value = params.value;
  let icon = null;
  if (value === 1) {
    icon = <CircleOutlinedIcon color="success" fontSize="small" />;
  } else if (value === 2) {
    icon = <ChangeHistoryOutlinedIcon color="warning" fontSize="small" />;
  } else if (value === 3) {
    icon = <ClearOutlinedIcon color="error" fontSize="small" />;
  }
  const handleToggle = () => {
    let next;
    if (value === 1) next = 2;
    else if (value === 2) next = 3;
    else if (value === 3) next = 1;
    else next = 1; // 未選択時は1から開始
    params.api.setEditCellValue({ id: params.id, field: params.field, value: next });
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <IconButton onClick={handleToggle} size="small" sx={{ width: 36, height: 36 }}>
        {icon}
      </IconButton>
    </div>
  );
}

// DataGrid編集セル: 取材場所（GoogleMap検索API連携）
const InterviewPlaceEditCell = (props) => {
  // ul要素のref
  const ulRef = React.useRef(null);
  const { value, api, id, field, setRows } = props;
  const theme = useTheme();
  const [inputValue, setInputValue] = React.useState(value || "");
  const [suggestions, setSuggestions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showList, setShowList] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [selectedIdx, setSelectedIdx] = React.useState(-1); // 行選択用
  const autocompleteService = React.useRef(null);
  const sessionToken = React.useRef(null);
  const placesService = React.useRef(null);

  // GoogleMap API AutocompleteService取得
  const getAutocompleteService = () => {
    if (!window.google) return null;
    if (!autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!sessionToken.current) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return autocompleteService.current;
  };

  // GoogleMap API  PlacesService取得
  const getPlacesService = () => {
    if (!window.google) return null;
    if (!placesService.current) {
      placesService.current = new window.google.maps.places.PlacesService(document.createElement("div"));
    }
    if (!sessionToken.current) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return placesService.current;
  };

  // EntryListPageから制御するためのref（useState宣言の後に記述）
  React.useImperativeHandle(props.ref, () => ({
    closeSuggestionList: () => {
      setShowList(false);
      setSuggestions([]);
      setSelectedIdx(-1);
    }
  }), [setShowList, setSuggestions, setSelectedIdx]);

  // 入力変更時は候補リストを表示しない
  const prevInputRef = React.useRef("");
  useEffect(() => {
    if (!inputValue || inputValue.trim() === "") {
      setShowList(false);
      setSuggestions([]);
      setSelectedIdx(-1);
    }
    prevInputRef.current = inputValue;
  }, [inputValue]);

  // Downキーで候補リスト操作
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (showList) {
        if (selectedIdx >= 0 && selectedIdx < suggestions.length) {
          // 候補リストで選択中の行を確定
          handleSelect(suggestions[selectedIdx]);
        }
        setShowList(false);
        setSuggestions([]);
        setSelectedIdx(-1);
        e.preventDefault();
        e.stopPropagation(); // DataGridにEnterキーを渡さない
        return;
      } else {
        // 候補リスト非表示の場合、入力内容をそのまま反映
        // （候補リストにない入力値を入れたときの対応）
        api.setEditCellValue({ id, field, value: inputValue });
        // DataGridのデフォルトEnter動作（行確定）は止めない
      }
    }
    if (e.key === 'Escape') {
      // Escキーで候補リストを閉じる（入力値はそのまま）
      if (showList) {
        setShowList(false);
        setSuggestions([]);
        setSelectedIdx(-1);
        e.preventDefault();
        e.stopPropagation(); // DataGridにEscキーを渡さない
        return;
      }
    }
    if (e.key === 'ArrowDown') {
      if (!showList) {
        // 候補リストが非表示ならサジェスト取得して表示
        handleSearch().then(() => {
          setShowList(true);
          setSelectedIdx(0);
        });
        e.preventDefault();
        return;
      }
      setSelectedIdx(prev => {
        const next = prev < suggestions.length - 1 ? prev + 1 : 0;
        return next;
      });
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      if (!showList) {
        // 候補リストが非表示ならサジェスト取得して表示
        handleSearch().then(() => {
          if (suggestions.length > 0) {
            setShowList(true);
            setSelectedIdx(suggestions.length - 1);
          }
        });
        e.preventDefault();
        return;
      }
      setSelectedIdx(prev => {
        const next = prev > 0 ? prev - 1 : suggestions.length - 1;
        return next;
      });
      e.preventDefault();
    }
  };

  // 選択行が隠れている場合は自動スクロール
  useEffect(() => {
    if (!showList || selectedIdx < 0) return;
    const ul = ulRef.current;
    if (!ul) return;
    const li = ul.children[selectedIdx];
    if (li && typeof li.scrollIntoView === 'function') {
      li.scrollIntoView({ block: 'nearest' });
    }
  }, [showList, selectedIdx]);

  // Google Maps APIでサジェスト取得
  const handleSearch = async () => {
    console.log("サジェスト検索1: " + inputValue);
    console.log("google", window.google);
    console.log("maps", window.google?.maps);
    console.log("places", window.google?.maps?.places);    
    // GoogleMap APIのAutocompleteService取得
    const service = getAutocompleteService();
    if (!service) return;
    console.log("サジェスト検索2: " + inputValue);
    setLoading(true);
    // サジェスト取得
    service.getPlacePredictions({ 
      input: inputValue, 
      componentRestrictions: { country: "JP" }, 
      sessionToken: sessionToken.current
    },
      (preds, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setSuggestions(Array.isArray(preds) ? preds.slice(0, 10) : []);
          setShowList(true);
          setSelectedIdx(-1);
        } else {
          setSuggestions([]);
          setShowList(false);
        }
        setLoading(false);
      }
    );
  };
    
  // サジェスト選択（Google Maps APIのsuggestion → place詳細取得）
  const handleSelect = async (pred) => {
    // サジェストリストを閉じる
    setShowList(false);
    setSuggestions([]);
    setIsFocused(false);
    // Google Maps APIのPlacesService取得
    const service = getPlacesService();
    if (!service) return;
    // 位置情報取得
    service.getDetails({ 
      placeId: pred.place_id, 
      fields: ["name", "geometry"], 
      sessionToken: sessionToken.current
    },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;
        // 位置と名称取得
        const lat = place.geometry.location.lat();
        const lon = place.geometry.location.lng();
        const placeName = place.name;
        // 入力値を更新
        setInputValue(placeName);
        // lat/lon/取材場所カラムも同時に更新（setRowsで直接更新）
        setRows(prevRows => prevRows.map(row => row.id === id ? { ...row, lat, lon, [field]: placeName } : row));
        // 編集セルにも即時反映
        api.setEditCellValue({ id, field, value: placeName });
        // 次の検索のために token を更新
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    );
  };

  // フォーカスが外れたら候補リストを非表示
  useEffect(() => {
    if (!isFocused) {
      setShowList(false);
      setSuggestions([]);
      setSelectedIdx(-1);
    }
  }, [isFocused]);

  // inputの位置を取得してulの表示位置を決定
  const inputRef = React.useRef(null);
  const [listPos, setListPos] = React.useState({ top: 0, left: 0, width: 400, direction: 'bottom' });
  useEffect(() => {
    if (showList && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      // DataGridの位置・高さ取得
      const grid = document.querySelector('.MuiDataGrid-root');
      let gridRect = { top: 0, height: window.innerHeight };
      if (grid) {
        gridRect = grid.getBoundingClientRect();
      }
      // inputの中心Y座標
      const inputCenterY = rect.top + rect.height / 2;
      // DataGridの中心Y座標
      const gridCenterY = gridRect.top + gridRect.height / 2;
      // direction: inputが上半分なら下、下半分なら上
      const direction = inputCenterY < gridCenterY ? 'bottom' : 'top';
      setListPos({
        top: direction === 'bottom' ? rect.bottom : rect.top,
        left: rect.left,
        width: rect.width,
        direction,
      });
    }
  }, [showList]);

  // レンダリング
  return (
    <div style={{ width: '100%', minWidth: 0, maxWidth: '100%', position: 'relative', overflow: 'visible', display: 'flex', alignItems: 'center', flex: 1 }}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={e => {
          setInputValue(e.target.value);
          setShowList(false);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={e => {
          if (e.relatedTarget && e.relatedTarget.classList && e.relatedTarget.classList.contains('interview-place-suggestion')) return;
          // フォーカスが外れたタイミングで未コミットの入力値をDataGridへ反映（Enter不要でクリック移動可）
          if (inputValue !== value) {
            try {
              api.setEditCellValue({ id, field, value: inputValue });
            } catch(_) { /* noop */ }
          }
          setIsFocused(false);
        }}
        placeholder="場所を検索..."
        style={{
          width: '100%',
          height: 32,
          fontSize: '11pt',
          fontFamily: theme.typography.fontFamily,
          padding: '0 32px 0 5px', // 右にクリアボタン分の余白
          borderRadius: '5px',
          boxSizing: 'border-box',
          border: 'none',
          outline: 'none',
          background: 'transparent',
        }}
        autoFocus
      />
      {/* 検索ボタン */}
      <IconButton
        size="small"
        onClick={async () => {
          await handleSearch();
          // 検索後にinputへフォーカス
          if (inputRef.current) inputRef.current.focus();
        }}
        sx={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%)', height: 32, width: 32 }}
        aria-label="検索"
      >
        <svg width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99c.41.41 1.09.41 1.5 0s.41-1.09 0-1.5l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
      </IconButton>
      {/* クリアボタン */}
      <IconButton
        size="small"
        onClick={() => {
          setInputValue('');
          setShowList(false);
          setSuggestions([]);
          setSelectedIdx(-1);
          setRows(prevRows => prevRows.map(row => row.id === id ? { ...row, lat: null, lon: null, [field]: '' } : row));
          // 編集セルにも即時反映
          api.setEditCellValue({ id, field, value: '' });
          // クリア後にinputへフォーカス
          if (inputRef.current) inputRef.current.focus();
        }}
        sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', height: 32, width: 32 }}
        aria-label="クリア"
      >
        <svg width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11l4.89 4.89-4.89 4.89a1 1 0 1 0 1.41 1.41l4.89-4.89 4.89 4.89a1 1 0 0 0 1.41-1.41l-4.89-4.89 4.89-4.89a1 1 0 0 0 0-1.41z"/></svg>
      </IconButton>
      {showList && suggestions.length > 0 && ReactDOM.createPortal(
        <ul
          ref={ulRef}
          style={{
            position: 'fixed',
            ...(listPos.direction === 'bottom'
              ? { top: listPos.top }
              : { bottom: `calc(100vh - ${listPos.top}px)` }),
            left: listPos.left,
            width: listPos.width,
            maxHeight: 380,
            overflowY: 'auto',
            overflowX: 'auto',
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '5px',
            zIndex: 9999,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            margin: 0,
            padding: 0,
            listStyle: 'none',
          }}
          tabIndex={-1}
          className="interview-place-suggestion"
        >
          {suggestions.map((suggestion, idx) => (
            <li
              key={suggestion.place_id || suggestion.structured_formatting.main_text || idx}
              onMouseDown={e => {
                e.preventDefault();
                setShowList(false);
                setSuggestions([]);
                handleSelect(suggestion);
              }}
              onClick={e => {
                setShowList(false);
                setSuggestions([]);
              }}
              onMouseEnter={() => setSelectedIdx(idx)}
              style={{
                padding: '3px 5px',
                borderBottom: idx < suggestions.length - 1 ? '1px solid #eee' : 'none',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                background: selectedIdx === idx ? '#e3f2fd' : 'transparent',
              }}
            >
              <p style={{ margin: 0, padding: 0, fontSize: '14px', lineHeight: '24px', whiteSpace: 'nowrap' }}>{suggestion.structured_formatting.main_text || ''}</p>
              {suggestion.structured_formatting.secondary_text && (
                <p style={{ fontSize: '11px', color: '#888', margin: 0, padding: 0, lineHeight: '24px', whiteSpace: 'nowrap' }}>{suggestion.structured_formatting.secondary_text}</p>
              )}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

// DataGrid編集セル: 担当者（複数選択）
const UcvPersonsEditCell = (params) => {
  const { value, api, id, field, options = [] } = params;
  const selected = Array.isArray(value) ? value : [];
  const handleChange = (event) => {
    const newValue = event.target.value;
    api.setEditCellValue({ id, field, value: newValue });
  };
  // メニューの開閉状態を制御
  const [open, setOpen] = React.useState(false);
  // メニュー内キーハンドラ: Spaceでトグル、Enterは閉じるだけ（選択は変更しない）
  const handleMenuKeyDown = (e) => {
    if (e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      const activeEl = document.activeElement;
      const itemEl = activeEl && activeEl.closest ? activeEl.closest('[data-value]') : null;
      if (!itemEl) return;
      const vRaw = itemEl.getAttribute('data-value');
      const v = isNaN(Number(vRaw)) ? vRaw : Number(vRaw);
      const next = Array.isArray(selected) ? [...selected] : [];
      const idx = next.findIndex((x) => String(x) === String(v));
      if (idx >= 0) next.splice(idx, 1); else next.push(v);
      api.setEditCellValue({ id, field, value: next });
      return;
    }
  if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      return;
    }
  };
  return (
    <Box sx={{ height: '100%', width: '100%', minWidth: 0, display: 'flex', alignItems: 'center' }}>
      <Select
        multiple
        value={selected}
        onChange={handleChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        MenuProps={{
          MenuListProps: { onKeyDown: handleMenuKeyDown, onKeyDownCapture: handleMenuKeyDown },
          PaperProps: {
            sx: {
              // 1アイテム高さ目安36px (py:0.5), 5件分 + 余白
              maxHeight: 50 * 5 + 10,
              overflowY: 'auto'
            }
          }
        }}
        renderValue={(selectedIds) => {
          const names = (selectedIds || []).map((sid) => options.find(o => o.id === sid)?.name).filter(Boolean);
          return names.join(', ');
        }}
        size="small"
        fullWidth
        variant="outlined"
        sx={{
          height: 36,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          fontSize: 'inherit',
          fontFamily: theme.typography.fontFamily,
          // 枠線を消す（通常・ホバー・フォーカス時）
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          // Selectトリガー: 縦中央 + はみ出し省略
          '& .MuiSelect-select': {
            display: 'block',
            py: 0,
            fontSize: 'inherit',
            fontFamily: theme.typography.fontFamily,
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            paddingRight: '28px', // ドロップダウンアイコン分の余白
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          // InputBase系の縦詰めを抑制
          '& .MuiInputBase-input': {
            py: 0,
            fontSize: 'inherit',
            fontFamily: theme.typography.fontFamily,
          },
          '& .MuiInputBase-root': {
            height: 36,
            display: 'flex',
            alignItems: 'center',
            fontSize: 'inherit',
            fontFamily: theme.typography.fontFamily,
            width: '100%',
            minWidth: 0,
          },
        }}
      >
        {options.map(opt => (
          <MenuItem key={opt.id} value={opt.id} data-value={opt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox size="small" checked={selected.indexOf(opt.id) > -1} />
            <ListItemText
              primary={opt.name}
              sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
              primaryTypographyProps={{ noWrap: true }}
            />
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

// DataGrid表示セル: 長いテキストを複数行で表示（内容・メモ用）
const MultiTextCell = React.memo(({ value }) => {
  const text = value || '';
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          whiteSpace: 'pre-wrap',
          //wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          lineHeight: '20px',
          maxHeight: '60px',
          width: '100%',
        }}
      >
        {text}
      </div>
    </div>
  );
});

// DataGrid編集セル: 長いテキストを複数行で編集（内容・メモ用）
const MultiTextEditCell = React.memo((params) => {
  const { id, field, value, api } = params;
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <TextField
        variant="standard"
        InputProps={{
          disableUnderline: true,
          sx: {
            fontSize: 'inherit',
            fontFamily: 'inherit',
            paddingLeft: '5px',
            paddingRight: '5px',
            background: 'none',
            boxShadow: 'none',
            '&:before': { borderBottom: 'none' },
            '&:after': { borderBottom: 'none' },
            '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
          },
        }}
        multiline
        fullWidth
        minRows={1}
        maxRows={3}
        value={value || ''}
        onChange={(e) =>
          api.setEditCellValue({
            id,
            field,
            value: e.target.value,
          })
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.stopPropagation();
          }
        }}
        size="small"
        autoFocus
      />
    </div>
  );
});

// 必須項目共通定義（インライン化）
const INTERVIEW_REQUIRED_FIELDS = [
  { field: 'mark',               label: '取材' },
  { field: 'interview_date',     label: '取材日' },
  { field: 'interview_starttime',label: '開始時間' },
//  { field: 'interview_endtime',  label: '終了時間' },
//  { field: 'ucv_person_ids',     label: '担当者' },
  { field: 'interview_name',     label: '取材名' },
  { field: 'interview_place',    label: '取材場所' },
  { field: 'interview_contents', label: '内容' },
  { field: 'interview_memos',    label: 'メモ' },
];
// 必須項目のうち空白のものを返す（全て埋まっていればundefinedを返す）
const findEmptyRequiredField = (row, required = INTERVIEW_REQUIRED_FIELDS) => {
  return required.find(f => {
    const v = row?.[f.field];
    if (Array.isArray(v)) return v.length === 0;
    return v == null || String(v).trim() === '';
  });
};

// 予定登録ページ
const EntryListPage = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  // 候補リスト制御用ref（EntryListPageからInterviewPlaceEditCellへ渡す）
  const interviewPlaceEditCellRef = React.useRef(null);
  // DataGrid API参照用ref（useEffectより前に定義）
  const apiRef = useGridApiRef();
  // デフォルト1行
  const [ rows, setRows ] = useState([
    { 
      id: 0, mark: '', 
      interview_date: '', 
      interview_starttime: '', 
      interview_endtime: '', 
      interview_name: '', 
      interview_place: '', 
      interview_contents: '', 
      interview_memos: '', 
      ucv_person_ids: [], 
      lat: null, 
      lon: null 
    }
  ]);

  // 担当者（複数選択）用: ユーザリスト
  const [users, setUsers] = useState([]);
  useEffect(() => {
    // ユーザリスト取得
    fetchUserList();
  }, []);

  // ユーザリスト取得
  const fetchUserList = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${userURL}/fullnames/`);
      // ユーザリスト設定
      setUsers(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // ユーザリスト初期化
      setUsers([]);
    }
  }

  // 削除ダイアログ用
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const openDeleteDialog = (row) => { setDeleteTarget(row); setDeleteOpen(true); };
  const closeDeleteDialog = () => { setDeleteOpen(false); setDeleteTarget(null); };
  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !deleteTarget.id || deleteTarget.id === 0) {
      // 削除ダイアログを閉じる
      closeDeleteDialog();
      return;
    }
    // リクエストデータ作成
    const payload = { id: deleteTarget.id };
    // ユーザ情報が存在する場合
    if (props.user && props.user.username) {
      payload.change_account = props.user.username;
    }
    // フォームデータ追加
    const body = new FormData();
    body.append('props', JSON.stringify(payload));
    // 取材データ削除API呼び出し
    authFetch(`${apiURL}/interviews/delete`, { method: 'POST', body, actionName: 'RemoveInterview' })
      .then(() => {
        // 対象行をリストから削除
        setRows(prev => prev.filter(r => r.id !== deleteTarget.id));
        // 作成済みIDリスト(sessionStorage)からも削除
        try {
          const key = 'created_interview_ids';
          const saved = JSON.parse(sessionStorage.getItem(key) || '[]');
          const filtered = saved.filter(v => v !== deleteTarget.id);
          sessionStorage.setItem(key, JSON.stringify(filtered));
        } catch(_) { /* noop */ }
        // トースト表示
        showToast('削除しました', 'success');
      })
      .catch((e) => {
        // 削除エラー
        console.log(e);
        // トースト表示
        showToast('削除に失敗しました', 'error');
      })
      .finally(() => {
        // 削除ダイアログを閉じる
        closeDeleteDialog();
      });
  };

  // 空白行判定（必須項目が全て空）
  const isPlaceholderRow = (row) => {
    const requiredFields = ['mark', 'interview_date', 'interview_starttime', 'interview_endtime', 'interview_name', 'interview_place', 'ucv_person_ids'];
    return requiredFields.every(f => row[f] === undefined || row[f] === null || String(row[f]).trim() === '');
  };

  // 取材リスト
  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 50,
      hide: true,
      editable: false,
      sortable: false
    },   
    {
      field: 'mark',
      headerName: '取材',
      width: 40,
      type: 'singleSelect',
      valueOptions: [1, 2, 3],
      editable: true,
      sortable: false,
      renderCell: (params) => {
        switch (params.value) {
          case 1:
            return <CircleOutlinedIcon color="success" fontSize="small" />;
          case 2:
            return <ChangeHistoryOutlinedIcon color="warning" fontSize="small" />;
          case 3:
            return <ClearOutlinedIcon color="error" fontSize="small" />;
          default:
            return null;
        }
      },
      renderEditCell: MarkEditCell
    },
    {
      field: 'interview_date',
      headerName: '取材日',
      width: 132,
      editable: true,
      sortable: false,
      type: 'date',
      valueFormatter: (params) => {
        // 日付フォーマット
        const formatted = toDateFormat(params);
        return (formatted) ? formatted : '';
      },
    },
    {
      field: 'interview_starttime',
      headerName: '開始時間',
      width: 92,
      editable: true,
      sortable: false,
      type: 'time',
      renderEditCell: TimeEditCell
    },
    {
      field: 'interview_endtime',
      headerName: '終了時間',
      width: 92,
      editable: true,
      sortable: false,
      type: 'time',
      renderEditCell: TimeEditCell,
    },
    {
      field: 'ucv_person_ids',
      headerName: '担当者',
      width: 150,
      editable: true,
      sortable: false,
      renderCell: (params) => {
        const ids = Array.isArray(params.value) ? params.value : [];
        if (!ids.length) return null;
        const names = ids.map(id => users.find(u => u.id === id)?.name).filter(Boolean);
        return <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{names.join(', ')}</span>;
      },
      renderEditCell: (params) => <UcvPersonsEditCell {...params} options={users} />,
    },
    {
      field: 'interview_name',
      headerName: '取材名',
      width: 150,
      editable: true,
      sortable: false
    },
    {
      field: 'interview_place',
      headerName: '取材場所',
      width: 250,
      minWidth: 100,
      editable: true,
      sortable: false,
      resizable: true,
      flex: 0,
      renderEditCell: (params) => <InterviewPlaceEditCell {...params} setRows={setRows} ref={interviewPlaceEditCellRef} />
    },
    {
      field: 'interview_contents',
      headerName: '内容',
      width: 150,
      editable: true,
      sortable: false,
      renderCell: (params) => <MultiTextCell value={params.value} />,
      renderEditCell: (params) => <MultiTextEditCell {...params} />,
    },
    {
      field: 'interview_memos',
      headerName: 'メモ',
      width: 250,
      editable: true,
      sortable: false,
      renderCell: (params) => <MultiTextCell value={params.value} />,
      renderEditCell: (params) => <MultiTextEditCell {...params} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.id === 0) return null; // 新規行は非表示
        return (
          <div style={{display: 'flex', gap: 4, height: '100%', alignItems: 'center', justifyContent: 'center'}}>
            <IconButton
              className="row-action"
              size="small"
              aria-label="編集"
              onClick={(e) => { 
                e.stopPropagation();
                // 戻り時の復元フラグを設定
                sessionStorage.setItem('restore_after_details', '1');
                navigate(`details/${params.row.id}`, { state: { referrer: location.pathname } });
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              className="row-action"
              size="small"
              color="error"
              aria-label="削除"
              onClick={(e) => { e.stopPropagation(); openDeleteDialog(params.row); }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </div>
        );
      }
    }
  ];

  // DataGridが編集モードでない時、上下キーで行選択
  useEffect(() => {
    // キーダウンイベントハンドラ
    const handleGridKeyDown = (e) => {
      const grid = document.querySelector('.MuiDataGrid-root');
      if (!grid) return;
      // 編集セルが存在する場合は何もしない
      if (grid.querySelector('.MuiDataGrid-cell--editing')) return;
      // 上下キーで行選択
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!apiRef.current) return;
        // 選択行のIDを取得
        const selectedRows = apiRef.current.getSelectedRows();
        let selectedId = null;
        if (selectedRows.size > 0) {
          selectedId = Array.from(selectedRows.keys())[0];
        } else if (rows.length > 0) {
          selectedId = rows[0].id;
        }
        // 次の行のインデックス取得
        const idx = rows.findIndex(row => row.id === selectedId);
        let nextIdx = idx;
        if (e.key === 'ArrowDown') {
          nextIdx = idx < rows.length - 1 ? idx + 1 : 0;
        } else if (e.key === 'ArrowUp') {
          nextIdx = idx > 0 ? idx - 1 : rows.length - 1;
        }
        // 次の行のIDを取得
        const nextId = rows[nextIdx]?.id;
        if (nextId !== undefined) {
          // 選択・フォーカスを統一的に更新
          focusFirstCellOfRow(nextId);
          handleRowClick({ row: rows[nextIdx] });
        }
        e.preventDefault();
      }
    };
    // DataGridのキーダウンイベントを監視
    const grid = document.querySelector('.MuiDataGrid-root');
    if (grid) {
      grid.addEventListener('keydown', handleGridKeyDown);
    }
    return () => {
      if (grid) {
        grid.removeEventListener('keydown', handleGridKeyDown);
      }
    };
  }, [rows, apiRef]);

  // 初期ロード時にDataGridへフォーカスし先頭行を選択・フォーカス
  const initialFocusDoneRef = React.useRef(false);
  useEffect(() => {
    if (initialFocusDoneRef.current) return;
    if (!apiRef.current) return;
    if (!rows || rows.length === 0) return;
    initialFocusDoneRef.current = true;
    const firstId = rows[0].id;
    const firstField = (columns.find(c => !c.hide)?.field) || 'id';
    setTimeout(() => {
      try {
        if (apiRef.current) {
          apiRef.current.setRowSelectionModel([firstId]);
          apiRef.current.scrollToIndexes && apiRef.current.scrollToIndexes({ rowIndex: 0 });
          apiRef.current.setCellFocus(firstId, firstField);
        }
        const grid = document.querySelector('.MuiDataGrid-root');
        if (grid && typeof grid.focus === 'function') {
          grid.focus();
        }
      } catch (_) {
        // no-op
      }
    }, 50);
  }, [rows, apiRef, columns]);

  // トースト用state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastSeverity, setToastSeverity] = useState('warning');
  // トースト表示関数
  const showToast = (msg, severity = 'warning') => {
    setToastMsg(msg);
    setToastSeverity(severity);
    setToastOpen(true);
  }
  // トースト表示タイマー
  useEffect(() => {
    if (toastOpen) {
      const timer = setTimeout(() => setToastOpen(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastOpen]);

  // 編集セルが外れた時（row edit mode終了時）に候補リストを閉じる
  const handleCellEditStop = () => {
    if (interviewPlaceEditCellRef.current) {
      interviewPlaceEditCellRef.current.closeSuggestionList && interviewPlaceEditCellRef.current.closeSuggestionList();
    }
  };

  // DataGridの行クリック
  const handleRowClick = (params) => {
    const row = params.row;
    // 今は何もしない
  };

  // タブキーでセル移動
  const handleCellKeyDown = (params, event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.defaultMuiPrevented = true;
      const colFields = columns.filter(col => !col.hide).map(col => col.field);
      const currentIdx = colFields.indexOf(params.field);
      let nextIdx;
      if (event.shiftKey) {
        nextIdx = currentIdx > 0 ? currentIdx - 1 : colFields.length - 1;
      } else {
        nextIdx = currentIdx < colFields.length - 1 ? currentIdx + 1 : 0;
      }
      const nextField = colFields[nextIdx];
      if (apiRef.current && nextField) {
        apiRef.current.setCellFocus(params.id, nextField);
        // Tab移動先が時間セルならinput[type='time']にfocus
        if (nextField === 'interview_starttime' || nextField === 'interview_endtime') {
          setTimeout(() => {
            const grid = document.querySelector('.MuiDataGrid-root');
            if (grid) {
              // data-fieldで対象セルを特定
              const cell = grid.querySelector(`.MuiDataGrid-cell--editing[data-field='${nextField}']`);
              if (cell) {
                const timeInput = cell.querySelector('input[type="time"]');
                if (timeInput) timeInput.focus();
              }
            }
          }, 0);
        }
        // Tab移動先がマークならbuttonにfocus
        else if (nextField === 'mark') {
          setTimeout(() => {
            const grid = document.querySelector('.MuiDataGrid-root');
            if (grid) {
              // data-fieldで対象セルを特定
              const cell = grid.querySelector(`.MuiDataGrid-cell--editing[data-field='${nextField}']`);
              if (cell) {
                const markButton = cell.querySelector('button');
                if (markButton) markButton.focus();
              }
            }
          }, 0);
        }
        // Tab移動先が取材場所なら入力Boxにfocus
        else if (nextField === 'interview_place') {
          setTimeout(() => {
            const grid = document.querySelector('.MuiDataGrid-root');
            if (grid) {
              // data-fieldで対象セルを特定
              const cell = grid.querySelector(`.MuiDataGrid-cell--editing[data-field='${nextField}']`);
              if (cell) {
                const textInput = cell.querySelector('input[type="text"]');
                if (textInput) textInput.focus();
              }
            }
          }, 0);
        }
        // Tab移動先が担当者ならSelectにfocus
        else if (nextField === 'ucv_person_ids') {
          setTimeout(() => {
            const grid = document.querySelector('.MuiDataGrid-root');
            if (grid) {
              const cell = grid.querySelector(`.MuiDataGrid-cell--editing[data-field='${nextField}']`);
              if (cell) {
                const button = cell.querySelector('[role="combobox"]');
                if (button) button.focus();
              }
            }
          }, 0);
        }
      }
    }
  };

  // 任意の行の編集開始時はその行の先頭セル（最初に見えている列）にフォーカスを合わせる
  const focusFirstCellOfRow = (rowId) => {
    if (!apiRef.current) return;
    const firstField = (columns.find(c => !c.hide)?.field) || 'id';
    // 対象行までスクロール
    const idx = rows.findIndex(r => r.id === rowId);
    if (idx >= 0 && apiRef.current.scrollToIndexes) {
      apiRef.current.scrollToIndexes({ rowIndex: idx });
    }
    // レンダリング反映後に選択とフォーカスを適用
    setTimeout(() => {
      if (!apiRef.current) return;
      apiRef.current.setRowSelectionModel([rowId]);
      apiRef.current.setCellFocus(rowId, firstField);
      if (firstField === 'mark') {
        setTimeout(() => {
          const grid = document.querySelector('.MuiDataGrid-root');
          if (grid) {
            const cell = grid.querySelector('.MuiDataGrid-cell--editing[data-field="mark"]');
            if (cell) {
              const markButton = cell.querySelector('button');
              if (markButton) markButton.focus();
            }
          }
        }, 0);
      }
    }, 0);
  };

  // 編集開始時にその行の先頭セルにフォーカスを合わせる
  const handleCellEditStart = (params, event) => {
    try {
      if (params && params.id != null) {
        focusFirstCellOfRow(params.id);
      }
    } catch (_) { /* noop */ }
  };

  // 行編集モード開始時にその行の先頭セルにフォーカスを合わせる
  const handleRowEditStart = (params, event) => {
    try {
      if (params && params.id != null) {
        focusFirstCellOfRow(params.id);
      }
    } catch (_) { /* noop */ }
  };

  // 編集内容反映（新規: create / 既存: update）
  const handleRowEdit = async (newRow, oldRow) => {
    // 共通必須項目（requiredFields.js）
    const requiredFields = INTERVIEW_REQUIRED_FIELDS;
    // 新規登録行
    if (newRow.id === 0) {
      // 空白項目があるかチェック
      const emptyField = findEmptyRequiredField(newRow, requiredFields);
      if (emptyField) {
        showToast(`${emptyField.label}が未入力です`, 'warning');
        return newRow; // そのまま編集継続
      }
      // 登録行作成
      const createRow = { ...newRow };
      // 日付フォーマット
      createRow.interview_date = toDateFormat(createRow.interview_date);
      // ユーザ情報が存在する場合
      if (props.user) {
        createRow.entry_account = props.user.username;
      }
      // 終了時間が未入力の場合は null に統一（API で空文字エラー回避）
      if (!createRow.interview_endtime || String(createRow.interview_endtime).trim() === '') {
        createRow.interview_endtime = null;
      }
      // フォームデータ作成
      const body = new FormData();
      body.append('props', JSON.stringify(createRow));
      try {
        // API呼び出し
        const res = await authFetch(`${apiURL}/interviews/create`, { method: 'POST', body, actionName: 'EntryInterview' });
        const result = await res.json();
        if (result?.id) {
          // 既存0行を結果IDに差し替え + 新しい空行を先頭追加
          setRows(prev => {
            const replaced = prev.map(r => r.id === 0 ? { ...newRow, id: result.id } : r);
            return [
              { 
                id: 0, 
                mark: '', 
                interview_starttime: '', 
                interview_endtime: '', 
                interview_date: '', 
                interview_name: '', 
                interview_place: '', 
                interview_contents: '', 
                interview_memos: '', 
                ucv_person_ids: [], 
                lat: null, 
                lon: null 
              },
              ...replaced
            ];
          });
          // 保存IDリスト更新
          try {
            const key = 'created_interview_ids';
            const saved = JSON.parse(sessionStorage.getItem(key) || '[]');
            if (Array.isArray(saved)) {
              const without = saved.filter(v => v !== result.id);
              sessionStorage.setItem(key, JSON.stringify([result.id, ...without]));
            } else {
              sessionStorage.setItem(key, JSON.stringify([result.id]));
            }
          } catch (_) { /* noop */ }
          // 新しい空行(id:0)へフォーカス
          setTimeout(() => {
            if (apiRef.current) {
              // 先頭行へスクロール & 選択
              if (apiRef.current.scrollToIndexes) apiRef.current.scrollToIndexes({ rowIndex: 0 });
              apiRef.current.setRowSelectionModel([0]);
              // 新しい空行を即座に行編集モードへ（ユーザが連続入力できるように）
              try { apiRef.current.startRowEditMode({ id: 0 }); } catch(_) { /* noop */ }
              // 先頭セル(mark)へフォーカス
              try { apiRef.current.setCellFocus(0, 'mark'); } catch(_) { /* noop */ }
              // 内部のボタンに確実にフォーカス（レンダリング完了を待つ）
              setTimeout(() => {
                try {
                  const grid = document.querySelector('.MuiDataGrid-root');
                  const markCell = grid?.querySelector(".MuiDataGrid-cell[data-field='mark'][data-id='0']") || grid?.querySelector(".MuiDataGrid-cell--editing[data-field='mark']");
                  const btn = markCell?.querySelector('button');
                  if (btn) btn.focus();
                } catch(_) { /* noop */ }
              }, 30);
            }
          }, 80);
          // トースト表示
          showToast('登録されました', 'success');
          // 新しい行を返す
          return { ...createRow, id: result.id };
        }
        // トースト表示
        showToast('登録に失敗しました', 'error');
      }
      catch (e) {
        // 登録エラー
        console.log(e);
        // トースト表示
        showToast('登録に失敗しました', 'error');
      }
      // 編集モード終了後の状態を反映したいので newRow を返す
      return newRow;
    }

    // 既存行の更新
    const normalize = (row) => ({
      mark: row?.mark ?? '',
      interview_date: toDateFormat(row?.interview_date) || '',
      interview_starttime: (row?.interview_starttime ?? '').toString().trim(),
      interview_endtime: (row?.interview_endtime ?? '').toString().trim(),
      interview_name: (row?.interview_name ?? '').toString().trim(),
      interview_place: (row?.interview_place ?? '').toString().trim(),
      interview_contents: (row?.interview_contents ?? '').toString().trim(),
      interview_memos: (row?.interview_memos ?? '').toString().trim(),
      ucv_person_ids: Array.isArray(row?.ucv_person_ids) ? [...row.ucv_person_ids].map(v => Number(v)).filter(v => !isNaN(v)).sort((a,b)=>a-b) : [],
      lat: (row?.lat == null || row?.lat === '') ? null : Number(row.lat),
      lon: (row?.lon == null || row?.lon === '') ? null : Number(row.lon),
    });
    // 変更がない場合は更新しない
    try {
      const oldNorm = normalize(oldRow);
      const newNorm = normalize(newRow);
      const noChange = Object.keys(newNorm).every(k => JSON.stringify(newNorm[k]) === JSON.stringify(oldNorm[k]));
      if (noChange) return oldRow; // 変更なし
    } catch (_) { /* noop */ }
    // 空白項目があるかチェック
    const emptyField = findEmptyRequiredField(newRow, requiredFields);
    if (emptyField) {
      showToast(`${emptyField.label}が未入力です`, 'warning');
      return oldRow;
    }
    // 更新行作成
    const editRow = { ...newRow };
    // 日付フォーマット
    editRow.interview_date = toDateFormat(editRow.interview_date);
    // 終了時間が未入力の場合は null （API 仕様対応）
    if (!editRow.interview_endtime || String(editRow.interview_endtime).trim() === '') {
      editRow.interview_endtime = null;
    }
    // ユーザ情報が存在する場合
    if (props.user) {
      editRow.change_account = props.user.username;
    }
    // フォームデータ作成
    const body = new FormData();
    body.append('props', JSON.stringify(editRow));
    try {
      // API呼び出し
      const res = authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'EditInterview' });
      // 成功時 state 更新（内容は newRow で既に楽観的更新済みなのでそのまま）
      setRows(prev => prev.map(r => r.id === newRow.id ? { ...r, ...newRow } : r));
      // トースト表示
      showToast('更新されました', 'success');
    }
    catch (e) {
      // 更新エラー
      console.log(e);
      // トースト表示
      showToast('更新に失敗しました', 'error');
    }
    // 編集モード終了後の状態を反映したいので newRow を返す
    return newRow;
  };

  // IDリストによる復元（詳細画面から戻ってきた場合のみ）
  useEffect(() => {
    // 復元が必要かチェック
    const needRestore = sessionStorage.getItem('restore_after_details') === '1';
    if (!needRestore) {
      // 別画面（詳細以外）から来た場合は作成IDを破棄
      sessionStorage.removeItem('created_interview_ids');
      return;
    }
    // 復元フラグをクリア
    sessionStorage.removeItem('restore_after_details');
    // 保存されたIDリスト取得 (sessionStorage)
    let ids = [];
    try {
      ids = JSON.parse(sessionStorage.getItem('created_interview_ids') || '[]');
      if (!Array.isArray(ids)) ids = [];
    } catch(_) { ids = []; }
    if (!ids.length) return;
    // APIで各IDの最新データ取得
    (async () => {
      try {
        // ID数分のAPIリクエストを並列実行
        const fetched = await Promise.all(ids.map(async (id) => {
          try {
            // API呼び出し
            return authJson(`${apiURL}/interviews/${id}`);
          } catch(_) { return null; }
        }));
        // 有効なデータのみ抽出
        const rowsData = fetched.filter(Boolean);
        if (rowsData.length) {
          // 取得したデータを整形
          const simplified = rowsData.map(r => {
            return {
              id: r.id,
              mark: r.mark ?? '',
              interview_starttime: r.interview_starttime ?? '',
              interview_endtime: r.interview_endtime ?? '',
              interview_date: (r.interview_date == undefined || r.interview_date == null) ? '' : new Date(r.interview_date),
              interview_name: r.interview_name ?? '',
              interview_place: r.interview_place ?? '',
              interview_contents: r.interview_contents ?? '',
              interview_memos: r.interview_memos ?? '',
              ucv_person_ids: Array.isArray(r.ucv_person_ids) ? r.ucv_person_ids : [],
              lat: (r.lat === undefined || r.lat === null || r.lat === '') ? null : r.lat,
              lon: (r.lon === undefined || r.lon === null || r.lon === '') ? null : r.lon,
            };
          });
          // 最初の空行を追加し、以降は取得データをID順にセット
          setRows([
            { id: 0, mark: '', interview_starttime: '', interview_endtime: '', interview_date: '', interview_name: '', interview_place: '', ucv_person_ids: [], lat: null, lon: null },
            ...simplified
          ]);
        }
      } catch(_) { /* noop */ }
    })();
  }, []);

  // レンダリング
  return (
    <Container>
      {/* 上部: タイトル */}
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-start" sx={{ width: '100%', my: 3, px: 3 }}>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>予定登録</Typography>
          <Typography variant="inherit" color="text.disabled">取材案件は"〇"、保留案件は"△"、取材不要は"×"</Typography>
        </Box>
      </Box>
      {/* 下部: DataGrid */}
      <Box display="flex" sx={{ width: '100%', height: '550px', px: 4, py: 0 }}>
        <ThemeDataGrid
          rows={rows}
          columns={columns}
          localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
          columnVisibilityModel={{ id: false }}
          processRowUpdate={handleRowEdit}
          onCellEditStart={handleCellEditStart}
          onRowEditStart={handleRowEditStart}
          onCellKeyDown={handleCellKeyDown}
          onRowClick={handleRowClick}
          onCellEditStop={handleCellEditStop}
          getRowClassName={(params) => (params.id === 0 && isPlaceholderRow(params.row)) ? 'placeholder-row' : ''}
          getRowHeight={() => 65} // 2行分の高さに設定
          experimentalFeatures={{ newEditingApi: true }}
          editMode="row"
          disableColumnMenu
          hideFooter
          sx={{
            cursor: 'pointer',
          }}
          apiRef={apiRef}
        />

        {/* 削除確認ダイアログ */}
        <Dialog open={deleteOpen} onClose={closeDeleteDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>予定の削除</Typography>
          </DialogTitle>
          <DialogContent sx={{pr:20}}>
            <DialogContentText id="alert-dialog-description">
              <Typography variant="inherit">
                {deleteTarget?.interview_name && String(deleteTarget.interview_name).trim() !== ''
                  ? `'${deleteTarget.interview_name}' を削除しますか？`
                  : 'この予定を削除しますか？'}
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{p:2}}>
            <Button onClick={closeDeleteDialog} variant="contained" style={{color: 'white', backgroundColor: 'gray'}} autoFocus>キャンセル</Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error">削除</Button>
          </DialogActions>
        </Dialog>
      </Box>
      {/* トースト（画面左下固定配置。DataGridの下） */}
      {toastOpen && (
        <Box
          sx={{
            position: 'fixed',
            left: 16,
            bottom: 16,
            zIndex: 2000,
            pointerEvents: 'none',
            maxWidth: 480,
            minWidth: 240,
          }}
        >
          <MuiAlert
            severity={toastSeverity ? toastSeverity : 'warning'}
            sx={{
              pointerEvents: 'auto',
              backgroundColor: theme => {
                const sev = toastSeverity ? toastSeverity : 'warning';
                if (sev === 'error') return theme.palette.error.main;
                if (sev === 'success') return theme.palette.success.main;
                if (sev === 'info') return theme.palette.info.main;
                return theme.palette.warning.main;
              },
              color: '#fff',
              '& .MuiAlert-icon': { color: '#fff' },
              boxShadow: 4,
            }}
          >
            {toastMsg}
          </MuiAlert>
        </Box>
      )}
    </Container>
  );
};

export default EntryListPage;